/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.chooseCountryBtn = this.querySelector('.choose-country-btn');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.domainNameLink = document.querySelector('.domain-name-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization || this.chooseCountryBtn) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        
        // 如果没有 slides,直接显示
        if (slides.length === 0) {
          if (this.slider.classList.contains('announcement__slider--vertical')) {
            this.slider.classList.add('is-initialized');
          }
          if (this.links) {
            this.moveLinks();
          }
          if (this.localization || this.chooseCountryBtn) {
            this.moveLocalization();
          }
          return;
        }
        
        // 如果只有一个公告，不需要滚动
        if (slides.length <= 1) {
          // 单个公告也要显示出来
          if (this.slider.classList.contains('announcement__slider--vertical')) {
            this.slider.classList.add('is-initialized');
          }
          if (this.links) {
            this.moveLinks();
          }
          if (this.localization || this.chooseCountryBtn) {
            this.moveLocalization();
          }
          return;
        }

        // 垂直滚动使用 transform
        // 创建包裹容器
        const wrapper = document.createElement('div');
        wrapper.classList.add('announcement__slider-wrapper');
        
        // 将所有原始 slides 移到 wrapper 中
        while (this.slider.firstChild) {
          wrapper.appendChild(this.slider.firstChild);
        }
        
        // 克隆所有slides以实现无限循环
        const originalSlides = wrapper.querySelectorAll('.announcement__text');
        originalSlides.forEach((slide) => {
          const clone = slide.cloneNode(true);
          clone.classList.add('announcement__text--cloned');
          wrapper.appendChild(clone);
        });
        
        // 将 wrapper 添加回 slider
        this.slider.appendChild(wrapper);
        
        const allSlides = wrapper.querySelectorAll('.announcement__text');
        let currentIndex = 0;
        allSlides[0].classList.add('is-visible');
        
        // 动态设置容器高度为所有 slides 中最高的高度
        const setSliderHeight = () => {
          let maxHeight = 0;
          // 只计算原始 slides 的高度（不包括克隆的）
          originalSlides.forEach((slide) => {
            const slideHeight = slide.offsetHeight;
            if (slideHeight > maxHeight) {
              maxHeight = slideHeight;
            }
          });
          
          if (maxHeight > 0) {
            this.slider.style.height = maxHeight + 'px';
            // 同时设置所有 slides 的最小高度，确保居中对齐
            allSlides.forEach((slide) => {
              slide.style.minHeight = maxHeight + 'px';
            });
            
            // 高度设置完成后，显示轮播
            this.slider.classList.add('is-initialized');
          }
        };
        
        // 初始设置高度
        setTimeout(setSliderHeight, 100);
        
        // 窗口大小改变时重新计算高度
        window.addEventListener('resize', setSliderHeight);

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const currSlide = allSlides[currentIndex];
            currentIndex++;

            // 无限循环：当到达克隆的slides时，无缝跳回到原始slides
            if (currentIndex >= allSlides.length) {
              currentIndex = 0;
            }

            const nextSlideElem = allSlides[currentIndex];
            
            // 使用 transform 实现垂直滚动动画
            // 动态获取每个 slide 的实际高度
            let totalHeight = 0;
            for (let i = 0; i < currentIndex; i++) {
              totalHeight += allSlides[i].offsetHeight;
            }
            const translateY = -totalHeight;
            wrapper.style.transform = `translateY(${translateY}px)`;
            
            // 当滚动到克隆的第一个元素时，无缝跳回到原始的第一个
            if (currentIndex === originalSlides.length) {
              setTimeout(() => {
                wrapper.style.transition = 'none';
                wrapper.style.transform = 'translateY(0)';
                currentIndex = 0;
                setTimeout(() => {
                  wrapper.style.transition = 'transform 0.5s ease-in-out';
                }, 50);
              }, parseInt(this.dataset.slideDelay) - 100);
            }

            nextSlideElem.classList.add('is-visible');
            currSlide.classList.remove('is-visible');
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization || this.chooseCountryBtn) {
        this.moveLocalization();
      }
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.domainNameLink = document.querySelector('.domain-name-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      const navBottomWrap = document.querySelector('.mb_nav_bottom_wrap');
      
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        if (!this.menu) return;
        
        const pcChoose = document.querySelector(".choose-country-btn");
        const hasContent = this.domainNameLink || pcChoose || (this.localization && this.localization.firstElementChild);
        
        if (!hasContent) return; // 没有任何内容需要移动
        
        const navBottomWrapEle = document.createElement('div');
        navBottomWrapEle.classList.add('mb_nav_bottom_wrap');
        
        // 添加域名链接（放在最上面）
        if (this.domainNameLink) {
          const mbDomainLink = document.createElement('div');
          mbDomainLink.classList.add("mb-domain-name-link");
          const domainLinkClone = this.domainNameLink.cloneNode(true);
          mbDomainLink.appendChild(domainLinkClone);
          navBottomWrapEle.appendChild(mbDomainLink);
          // 隐藏原始的域名链接
          this.domainNameLink.style.display = 'none';
        }
        
        // 只有当有国家选择器或语言选择器时才创建 flex 容器
        if (pcChoose || (this.localization && this.localization.firstElementChild)) {
          const localizationFlexContainer = document.createElement('div');
          localizationFlexContainer.classList.add('mb-localization-flex-container');
          
          // 添加国家选择器
          if (pcChoose) {
            const mbChooseCountry = document.createElement('div');
            mbChooseCountry.classList.add("mb-choose-country-btn");
            mbChooseCountry.innerHTML = pcChoose.innerHTML;
            mbChooseCountry.addEventListener("click", () => {
              pcChoose.click();
            });
            localizationFlexContainer.appendChild(mbChooseCountry);
          }
          
          // 添加语言选择器（只有存在时才添加）
          if (this.localization && this.localization.firstElementChild) {
            const mobLocalizationElem = document.createElement('div');
            mobLocalizationElem.classList.add('mob__localization');
            mobLocalizationElem.appendChild(this.localization.firstElementChild);
            localizationFlexContainer.appendChild(mobLocalizationElem);
          }
          
          navBottomWrapEle.appendChild(localizationFlexContainer);
        }
        
        this.menu.appendChild(navBottomWrapEle);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        if (this.localization && menuLocalization.firstElementChild) {
          this.localization.appendChild(menuLocalization.firstElementChild);
        }
        
        if (menuLocalization) {
          menuLocalization.remove();
        }
        
        if (navBottomWrap) {
          navBottomWrap.remove();
        }
        
        // 恢复显示原始的域名链接
        if (this.domainNameLink) {
          this.domainNameLink.style.display = '';
        }
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
